/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import { IProduct } from '../manageProduct/types';
import { StyleField } from './types';
import { AddOnFields, AddOnFieldsResponse } from './types';

const getCategories = async () => {
  const response = await AxiosInstance.get(api.product.categories);
  return response.data;
};

const useGetCategories = () => {
  return useQuery('categories', getCategories);
};

const getSubCategories = async (id: number) => {
  const response = await AxiosInstance.get(api.product.subCategories.replace('id', id.toString()));
  return response.data;
};

const useGetSubCategories = (id: number) => {
  return useQuery({
    queryKey: ['subCategories', id],
    queryFn: () => getSubCategories(id),
    enabled: !!id,
  });
};

const getBrands = async () => {
  const response = await AxiosInstance.get(api.product.brands);
  return response.data;
};

const useGetBrands = () => {
  return useQuery('brands', getBrands);
};

const getStyleFields = async (): Promise<StyleField[]> => {
  const response = await AxiosInstance.get(api.product.styleFields);
  return response.data;
};

export const useGetStyleFields = () => {
  return useQuery<StyleField[]>({
    queryKey: ['styleFields'],
    queryFn: getStyleFields,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
  });
};

const getAddOnFields = async (): Promise<AddOnFields[]> => {
  try {
    const response = await AxiosInstance.get<AddOnFieldsResponse>(api.product.addOnFields);
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

export const useGetAddOnFields = () => {
  return useQuery<AddOnFields[], Error>({
    queryKey: ['addOnFields'],
    queryFn: getAddOnFields,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

const getColors = async () => {
  const response = await AxiosInstance.get(api.product.colors);
  return response.data;
};

const useGetColors = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['colors'],
    queryFn: getColors,
    enabled,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

const getSizes = async () => {
  const response = await AxiosInstance.get(api.product.sizes);
  return response.data;
};

const useGetSizes = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['sizes'],
    queryFn: getSizes,
    enabled,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

const getCollections = async () => {
  const response = await AxiosInstance.get(api.product.collection);
  return response.data;
};

const useGetCollections = () => {
  return useQuery('collection', getCollections);
};

const getFabrics = async () => {
  const response = await AxiosInstance.get(api.product.fabrics);
  return response.data;
};

const useGetFabrics = () => {
  return useQuery('fabrics', getFabrics);
};

export interface IMeasurementField {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
}

const createAddOn = async (formData: FormData) => {
  const response = await AxiosInstance.post('catalog/api/v1/vendor/product/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const useCreateAddOn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAddOn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      queryClient.invalidateQueries({ queryKey: ['addOns'] });
      queryClient.invalidateQueries({ queryKey: ['productCount'] });
    },
  });
};

const updateAddOn = async ({ id, payload }: { id: string | number; payload: FormData }) => {
  const response = await AxiosInstance.patch(`catalog/api/v1/vendor/product/${id}/`, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const useUpdateAddOn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAddOn,
    onSuccess: (_, variables) => {
      // Get variables from mutation
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      queryClient.invalidateQueries({ queryKey: ['addOns'] });
      queryClient.invalidateQueries({ queryKey: ['addOnDetail', String(variables.id)] });
      queryClient.invalidateQueries({ queryKey: ['productCount'] });
    },
  });
};

const removeVariant = async (variant_ids: number[]) => {
  const response = await AxiosInstance.delete(api.product.removeVariant, {
    data: { variant_ids },
  });
  return response.data;
};

const useRemoveVariant = () => {
  return useMutation({
    mutationFn: (variant_ids: number[]) => removeVariant(variant_ids),
  });
};

const removeVariantSize = async (variantIds: number[]) => {
  const response = await AxiosInstance.delete(api.product.removeVariantSize, {
    data: { variant_ids: variantIds },
  });
  return response.data;
};

const useRemoveVariantSize = () => {
  return useMutation({
    mutationFn: (variantIds: number[]) => removeVariantSize(variantIds),
  });
};

const getProductById = async (id: string): Promise<IProduct> => {
  const response = await AxiosInstance.get(api.manageProduct.product_detail.replace(':id', id));
  return response.data;
};

const useGetProductDetail = (id: string | undefined): UseQueryResult<IProduct> => {
  return useQuery({
    queryKey: ['product_detail', id],
    queryFn: () => getProductById(id!),
    enabled: !!id && id !== '0' && id !== '',
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

const removeImage = async (imageId: number | undefined) => {
  const response = await AxiosInstance.delete(`${api.product.removeVariantImage}${imageId}/`);
  return response.data;
};

const useRemoveImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => removeImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_detail'] });
    },
  });
};

const getCurrencyByLocation = async (): Promise<{ currency: string }> => {
  const response = await AxiosInstance.get(api.product.getCurrency);
  return response.data;
};

const useGetCurrency = () => {
  return useQuery({
    queryKey: ['currency'],
    queryFn: getCurrencyByLocation,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

const deleteAddOnVariant = async (variantId: string) => {
  // variantId is UUID string → no need to convert to number
  const url = `/catalog/api/v2/vendor/add-on/variant/${variantId}/`;

  const response = await AxiosInstance.delete(url);
  return response.data;
};

export const useDeleteAddOnVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAddOnVariant,
    onSuccess: () => {
      // Optional: invalidate specific add-on detail if you have the add-on id
      queryClient.invalidateQueries({ queryKey: ['addOnDetail'] });
      queryClient.invalidateQueries({ queryKey: ['addOns'] });
      // If add-ons are tied to products → also invalidate product detail
      // queryClient.invalidateQueries({ queryKey: ['product_detail'] });
    },
    onError: (error: unknown) => {
      console.error('Delete add-on variant failed:', error);
    },
  });
};

const deleteAddOnSize = async (sizeId: number) => {
  const response = await AxiosInstance.delete(`/catalog/api/v2/vendor/add-on/size/${sizeId}/`);
  return response.data;
};

export const useDeleteAddOnSize = () => {
  return useMutation({
    mutationFn: (sizeId: number) => deleteAddOnSize(sizeId),
    onError: (error) => {
      console.error('Delete add-on size failed:', error);
    },
  });
};
export {
  useGetBrands,
  useGetColors,
  useGetCategories,
  useGetFabrics,
  useGetSizes,
  useGetSubCategories,
  useGetCollections,
  useRemoveVariant,
  useRemoveVariantSize,
  useRemoveImage,
  useGetProductDetail,
  useGetCurrency,
};
